/*
 * Average ball speed per shot type — seven horizontal rows on ONE shared
 * linear scale, so the bars compare against each other rather than each
 * filling its own track.
 *
 * Types without a measurement are omitted instead of adding empty rows. Rows
 * built from 1-3 measured shots stay visible but are marked as a small sample
 * with a hatched bar, a warning glyph, the sample count and a permanently
 * visible footnote — the warning never depends on color or on hovering.
 */

import type React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Swords, Shield } from 'lucide-react';
import type { ShotSpeedRow } from './shotAnalytics';
import { SHOT_TYPE_CATALOG } from './shotTypes';
import { EntityAvatar } from './EntityAvatar';

export interface ShotSpeedCompareEntity {
  id: string;
  name: string;
  color: string;
  crop?: string;
  crops?: string[];
  rows: ShotSpeedRow[] | null;
}

const LABEL_BY_KEY = new Map(
  SHOT_TYPE_CATALOG.map((shotType) => [shotType.key, shotType.lbl]),
);
const GROUP_BY_KEY = new Map(
  SHOT_TYPE_CATALOG.map((shotType) => [shotType.key, shotType.group]),
);

// A measured row always paints a visible stub, so a very slow shot still reads
// as "measured" next to the fastest one.
const MIN_FILL_PCT = 6;

const ShotSpeedComparison: React.FC<{
  entities: ShotSpeedCompareEntity[];
  scopeNote?: string;
}> = ({ entities, scopeNote }) => {
  const { t } = useTranslation('tournament');
  const groups = SHOT_TYPE_CATALOG.flatMap((shotType) => {
    const rows = entities.flatMap((entity) => {
      const row = entity.rows?.find((item) => item.type === shotType.key);
      return row?.value !== null && row?.value !== undefined
        ? [{ entity, row }]
        : [];
    });
    return rows.length ? [{ shotType, rows }] : [];
  });
  const hasLimited = groups.some((group) =>
    group.rows.some(({ row }) => row.state === 'limited'),
  );

  return (
    <div className="speed-bars speed-compare">
      {scopeNote ? <p className="speed-scope">{scopeNote}</p> : null}
      <div className="speed-compare-groups">
        {groups.map(({ shotType, rows }) => {
          const max = Math.max(...rows.map(({ row }) => row.value!), 0) || 1;
          const label = t(`label.${shotType.lbl}` as never) as string;
          return (
            <section key={shotType.key} className="speed-compare-group">
              <h4 className="cmp-metric-label">{label}</h4>
              <div className="speed-compare-rows">
                {rows.map(({ entity, row }) => (
                  <div key={entity.id} className="cmp-row speed-compare-row">
                    <div className="cmp-id">
                      <EntityAvatar
                        name={entity.name}
                        color={entity.color}
                        crop={entity.crop}
                        crops={entity.crops}
                      />
                      <span className="cmp-name">{entity.name}</span>
                    </div>
                    <div className="cmp-track">
                      <div
                        className="cmp-fill"
                        style={{ width: `${(row.value! / max) * 100}%` }}
                      />
                    </div>
                    <div className="cmp-val">
                      <span className="cmp-val-num">
                        {row.value!.toFixed(1)}
                      </span>
                      <span className="cmp-val-unit">{t('speed.unit')}</span>
                    </div>
                    {row.state === 'limited' ? (
                      <span className="speed-compare-flag">
                        <AlertTriangle
                          size={10}
                          strokeWidth={2.4}
                          aria-hidden
                        />
                        {
                          t('speed.samples', {
                            count: row.measurements,
                          }) as string
                        }
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
      {hasLimited ? (
        <p className="speed-note">
          <AlertTriangle size={12} strokeWidth={2.2} aria-hidden />
          <span>{t('speed.limitedNote')}</span>
        </p>
      ) : null}
    </div>
  );
};

const ShotSpeedBars: React.FC<{
  rows: ShotSpeedRow[];
  scopeNote?: string;
  comparison?: ShotSpeedCompareEntity[];
}> = ({ rows, scopeNote, comparison }) => {
  const { t } = useTranslation('tournament');

  if (comparison && comparison.length > 0) {
    return <ShotSpeedComparison entities={comparison} scopeNote={scopeNote} />;
  }

  // Fastest first. The rows arrive in radar order, which is meaningful on a
  // wheel but arbitrary in a ranked list — sorting makes the bars descend and
  // puts the hardest-hit stroke at the top where it's read first.
  const visibleRows = rows
    .filter((row) => row.value !== null)
    .sort((a, b) => b.value! - a.value!);
  const max = visibleRows.reduce(
    (peak, row) => (row.value! > peak ? row.value! : peak),
    0,
  );
  const hasLimited = visibleRows.some((row) => row.state === 'limited');

  return (
    <div className="speed-bars">
      {scopeNote ? <p className="speed-scope">{scopeNote}</p> : null}

      <ul className="speed-list">
        {visibleRows.map((row) => {
          const label = t(
            `label.${LABEL_BY_KEY.get(row.type) ?? row.type}` as never,
          ) as string;
          const pct =
            max > 0 ? Math.max(MIN_FILL_PCT, (row.value! / max) * 100) : 0;
          const group = GROUP_BY_KEY.get(row.type);
          return (
            <li key={row.type} className="speed-row">
              {/* The group is carried by the glyph alone — the same Swords /
                  Shield that mark the radar's two halves — so the label column
                  stays narrow and the bar keeps its width. */}
              <span className={`speed-name ${group ?? ''}`}>
                {group === 'offensive' ? (
                  <Swords size={10} strokeWidth={2.3} aria-hidden />
                ) : group === 'defensive' ? (
                  <Shield size={10} strokeWidth={2.3} aria-hidden />
                ) : null}
                {label}
              </span>
              <span className="speed-track">
                <span className="speed-fill" style={{ width: `${pct}%` }} />
              </span>
              <span className="speed-value">
                <span className="speed-num">{row.value!.toFixed(1)}</span>
                <span className="speed-unit">{t('speed.unit')}</span>
              </span>
              {row.state === 'limited' ? (
                <span className="speed-flag">
                  <AlertTriangle size={11} strokeWidth={2.4} aria-hidden />
                  <span>
                    {
                      t('speed.samples', {
                        count: row.measurements,
                      }) as string
                    }
                  </span>
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>

      {hasLimited ? (
        <p className="speed-note">
          <AlertTriangle size={12} strokeWidth={2.2} aria-hidden />
          <span>{t('speed.limitedNote')}</span>
        </p>
      ) : null}
    </div>
  );
};

export default ShotSpeedBars;
